import groq from 'groq';

import type { SemboMarket } from '../../utils/markets';
import { getLanguageSuffixByMarket } from './queries';

export const getFallbackIATAForMarket = (market: SemboMarket) => {
  return `*[_type == "market" && siteDomain == "${market.host}"][0].fallbackIATA`;
};

export const campaignDatePreferencesProjection = groq`{
  mode,
  preferredMonths,
  monthsFromNow,
  daysFromNow,
  minimumDaysFromNow,
  minimumDaysLeftInMonth,
  specificDates,
  "weekendDateConfig": weekendDateConfig {
    mode,
    preferredMonths,
    monthsFromNow,
    daysFromNow,
    minimumDaysFromNow,
    minimumDaysLeftInMonth,
    specificDates
  }
}`;

export const getImageQueryProperties = (
  market: SemboMarket,
  prefix = ''
) => groq`
    ...(${prefix}image.asset-> {
      "src": url,
      "path": path,
      "blurHash": metadata.blurHash,
    }),
    "description": ${prefix}description${getLanguageSuffixByMarket(market)},
    "focalPointX": ${prefix}focalPointX,
    "focalPointY": ${prefix}focalPointY,
    "watermarkText": ${prefix}watermarkText,
    "watermarkImage": ${prefix}watermarkImage -> image.asset->url
`;

const getReferencePrefix = (isReference?: boolean | string) => {
  let prefix = '';
  if (isReference === true) {
    prefix = '@->';
  } else if (typeof isReference === 'string') {
    prefix = isReference;
  }

  return prefix;
};

/**
 * Used to filter content by language and country.
 * NOTE: Use `contentDefinedByLocale` for *[] queries as it is much faster
 */
export const contentFilterByLocale = (
  market: SemboMarket,
  options?: {
    slug?: string;
    isReference?: boolean | string;
  }
) => {
  const language = getLanguageSuffixByMarket(market);
  const prefix = getReferencePrefix(options?.isReference);

  return `
    ${prefix}content${language}`;
};

/**
 * Used to find entries where a specific localized content is defined
 */
export const contentDefinedByLocale = (
  market: SemboMarket,
  options?: {
    slug?: string;
    isReference?: boolean | string;
    include?: Array<'discontinued' | 'expired'>;
    exclude?: 'unlisted';
    tagIds?: string[];
  }
) => {
  const language = getLanguageSuffixByMarket(market);
  const prefix = getReferencePrefix(options?.isReference);

  const tagsFilter =
    options?.tagIds && options.tagIds.length > 0
      ? ` && coalesce(count(${prefix}tags[_ref in [${options.tagIds.map((id) => JSON.stringify(id)).join(',')}]]), 0) == 0`
      : '';

  const slugFilter =
    typeof options?.slug === 'string'
      ? ` && ${prefix}content${language}.slug.current == ${JSON.stringify(options.slug)}`
      : '';

  const irrelevantAndRelevantcountryFilter =
    getIrrelevantAndRelevantCountryFilter(market, options);

  const discontinuedFilter = options?.include?.includes('discontinued')
    ? ''
    : ` && ${prefix}discontinued != true`;

  const expiredFilter = options?.include?.includes('expired')
    ? ''
    : ` && !(${isExpired({ isReference: options?.isReference })})`;

  const unlistedFilter =
    options?.exclude === 'unlisted' ? ` && ${prefix}unlisted != true` : '';

  // finally, use the base filter for content filtering on a much smaller subset
  const filterByLocale = contentFilterByLocale(market, options);

  return `
     ${filterByLocale}.enabled == true${`&& ${irrelevantAndRelevantcountryFilter}`}${slugFilter}${discontinuedFilter}${expiredFilter}${tagsFilter}${unlistedFilter}
    `;
};

/**
 * Returns a GROQ fragment for trip expiration check
 */
export const isExpired = ({
  isReference,
}: {
  isReference?: boolean | string;
} = {}): string => {
  const prefix = getReferencePrefix(isReference);
  return `defined(${prefix}expireAt) && ${prefix}expireAt < now()`;
};

export const getIrrelevantAndRelevantCountryFilter = (
  market: SemboMarket,
  options?: {
    isReference?: boolean | string;
  }
) => {
  const prefix = getReferencePrefix(options?.isReference);
  return `!("${market.countryISO}" in coalesce(${prefix}irrelevantCountries, [])) && ("${market.countryISO}" in coalesce(${prefix}countries, ["${market.countryISO}"]))`;
};
